import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';

import { GetCategoriesWithHeadlines, GetCategories } from '@root/modules/category/types/categories';
import getCategoriesWithHeadlines from '@root/modules/category/graphql/getCategoriesWithHeadlines.graphql';
import getCategories from '@root/modules/category/graphql/getCategories.graphql';

import { getRouteParams } from '@root/common/utils/url';
import { getPageOffset } from '@root/common/utils/getPageOffset';
import getChannelDomain from '@root/modules/channel/utils/getChannelDomain';

type ServiceVariables = {
  channel: string | string;
  parent?: string;
  parentCategoryId?: number;
  slug?: string;
  domain?: string | string[];
  language?: string;
  page?: string;
  headlinesLimit?: number | undefined;
  getCount?: boolean;
  getViews?: boolean;
};

type FetchVariables = GetCategoriesWithHeadlines['variables'];

export default class CategoryWithHeadlinesService extends Service implements ServiceInterface {
  private fixVariables({ channel, parent, parentCategoryId, slug, page, language, headlinesLimit }: ServiceVariables) {
    const params = getRouteParams({ parent, slug, desiredIdType: 'number' });
    const domain = getChannelDomain(channel);
    const limit = headlinesLimit || 50;

    const offset = getPageOffset(limit, page);
    const variables: GetCategoriesWithHeadlines['variables'] = {
      offset,
      limit,
      parent: params.parent,
      parentCategoryId,
      slug: params.slug,
      domain,
      authorLanguage: language,
      getCount: true,
      getViews: domain === 'www.delfi.lt/video',
    };

    return variables;
  }

  private async fetchCategoryFromSlug(variables: FetchVariables) {
    const apiProvider = this.createProvider('GraphQL');

    apiProvider
      .selectAPI('content-api-v3')
      .setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true })
      .setCacheOptions({ key: `getCategories:${JSON.stringify(variables)}`, ttl: 30 })
      .enableCache(true);

    const options = Object.assign({ query: getCategories }, { variables });
    const response = await apiProvider.request<GetCategories['data']>(options);

    if (!(response.categories.data.length > 0)) {
      this.handleError();
    }

    const category = response.categories.data[0];

    return category;
  }

  private async fetchCategoryWithHeadlines(variables: FetchVariables) {
    const apiProvider = this.createProvider('GraphQL');

    apiProvider
      .selectAPI('content-api-v3')
      .setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true })
      .setCacheOptions({ key: `fetchCategoryWithHeadlines:${JSON.stringify(variables)}`, ttl: 30 })
      .enableCache(true);

    const options = Object.assign({ query: getCategoriesWithHeadlines }, { variables });
    const response = await apiProvider.request<GetCategoriesWithHeadlines['data']>(options);

    if (!(response.categories.data.length > 0)) {
      this.handleError();
    }

    const category = response.categories.data[0];

    return {
      category,
      headlines: response.headlines,
    };
  }

  public handleError() {
    throw {
      message: 'category.error.not_found',
      statusCode: 404,
    };
  }

  public async fetch(variables: ServiceVariables) {
    const isHealthy = this.getHealthCheckStatus('content-api-v3');

    if (!isHealthy) {
      this.handleError();
    }

    if (variables.parent) {
      const domain = variables.domain as string;
      const parent = await this.fetchCategoryFromSlug({ slug: variables.parent, domain });
      if (parent) {
        variables.parentCategoryId = parent.id;
      }
    }

    const fixedVariables = this.fixVariables(variables);
    const response = await this.fetchCategoryWithHeadlines(fixedVariables);

    return response;
  }
}

export function fetchCategoryWithHeadlines(variables: ServiceVariables, { req }: { req: PortalRootIncomingMessage }) {
  const categoryWithHeadlines = new CategoryWithHeadlinesService({ req });
  return categoryWithHeadlines.fetch(variables);
}
